import { uploadAndTranscribeBulkAudio } from "../lib/supabaseClient";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { IDBPDatabase, openDB, DBSchema } from "idb";
import {
    verifyAudioBlobIntegrity,
    buildDeadBlobSentryExtra,
    type AudioIntegrityResult,
} from "../lib/audioBlobIntegrity";
import { getPreviousTabNonce, getTabNonce, isTabOpen, STALE_OWNER_MS } from "../lib/recorderPersistence";

// Defined beside SNAPSHOT_HEARTBEAT_MS, which it must stay well above; re-exported for recovery callers.
export { STALE_OWNER_MS };

export interface TranscriptSectionIndexDB {
    // Combination of transcriptId and transcriptSectionId
    audioData: Blob;
    fileName: string;
    createdAt: Date;
    bytesUploaded: number;
    uploadUrl: string | null;
    transcriptId: string;
    transcriptSectionId: string;
    patientRecordId: string;
    patientName: string;
    isUploading: boolean;
    // PR review finding: dual-purpose since EN-1217 -- counts BOTH a failed upload attempt
    // (retryIncompleteUploads' outer catch) AND a failed dead-blob delete attempt (the isolated
    // catch beside verifyAudioBlobIntegrity, see RETRY_LIMIT below). A row that already
    // accumulated retries from genuine upload failures reaches RETRY_LIMIT after fewer
    // dead-blob-delete attempts than a fresh row would, and vice versa. Not a correctness bug --
    // nothing reads this count for any purpose other than the RETRY_LIMIT gate, and no UI surfaces
    // it -- but the two failure classes are conflated under one budget; a future engineer adding a
    // third failure-count consumer should know this.
    retryCount: number;
    // The Monitoring document (chat_completions id) this recording was captured against, frozen
    // at recording-start (see RealtimeProvider's recordingMonitoringDocumentIdRef). Persisted so a
    // retry of this exact recording — possibly a whole browser session later, once the live ref
    // has moved on to something else — still targets the right Monitoring document instead of
    // silently reading whatever the live ref currently holds. Optional: rows written before this
    // field existed read back as `undefined`, same as "no target".
    targetMonitoringDocumentId?: string | null;
    // The clinic account (account.id) this recording was captured under, frozen at recording-start
    // (see useAudioRecorder's accountDataRef). EN-1171: without this, a retry run by
    // retryIncompleteUploads (no React/account context — startup or network-restore) or
    // LocalStorageModal (a retry that can run a session after capture) would stamp
    // transcript_sections.account_id with the current auth.uid() default instead of the clinic
    // account, hiding the recording from every other user on the account. Optional: rows written
    // before this field existed read back as `undefined`, same as "no account known".
    accountId?: string | null;
    // EN-1609: the tab (recorderPersistence getTabNonce) that wrote this row, and when. snapshotAt is
    // re-stamped on every write (every PERSIST_SNAPSHOT_MS while recording) and by touchSnapshot
    // (every SNAPSHOT_HEARTBEAT_MS while paused, when no writes happen). Recovery uses them to upload
    // a dead tab's snapshot while other tabs keep recording (see STALE_OWNER_MS).
    // Optional: rows written before these fields existed read back as `undefined` and keep the old
    // all-or-nothing rule.
    ownerTabNonce?: string;
    snapshotAt?: Date;
}

interface HappyDB extends DBSchema {
    transcriptSections: {
        key: string;
        value: TranscriptSectionIndexDB;
        indexes: { createdAtIndex: Date };
    };
}

/*
	Note: we could manage global isRecording and isUploading and block retry if true,
	but I don't think we need to since we are only auto-retrying on startup (refresh), and
	can assume there is no recording or uploading happening at that time.
*/

export const INDEX_DB_VERSION = 6;
export const AUDIO_DB_NAME = "AudioDatabase";
// Gates both upload-retry failures and (since EN-1217) dead-blob-delete failures against the same
// budget -- see the retryCount field comment above for why that's an accepted, documented smell
// rather than a bug.
const RETRY_LIMIT = 3;
const LOW_DISK_SPACE_THRESHOLD = 150; // 150MB

class AudioDb {
    private static instance: AudioDb;
    private static initPromise: Promise<void> | null = null;
    private db: IDBPDatabase<HappyDB> | null;
    private dbName: string;
    // Serializes recovery passes so overlapping callers (startup + network-restore) can't
    // double-upload the same incomplete recording. See retryIncompleteUploads.
    private retryInFlight = false;

    private constructor() {
        this.db = null;
        this.dbName = AUDIO_DB_NAME;
    }

    // singleton pattern with lazy initialization
    static getInstance(): AudioDb {
        if (!AudioDb.instance) {
            AudioDb.instance = new AudioDb();
        }
        return AudioDb.instance;
    }

    /**
     * EN-1502: reads incomplete recordings for the setup check without init()'s side effects (resetting
     * isUploading, cleanup, storage checks), so looking never disturbs an upload running in another tab.
     * Never creates the database, and closes its connection before returning.
     */
    static async readIncompleteReadOnly(): Promise<TranscriptSectionIndexDB[]> {
        if (typeof indexedDB.databases === "function") {
            const existing = await indexedDB.databases();
            if (!existing.some((d) => d.name === AUDIO_DB_NAME)) return [];
        }
        let missing = false;
        let db: IDBPDatabase<HappyDB>;
        try {
            // No version opens whatever is there; an upgrade means nothing was, so abort instead of creating it.
            db = await openDB<HappyDB>(AUDIO_DB_NAME, undefined, {
                upgrade(_db, _oldVersion, _newVersion, transaction) {
                    missing = true;
                    // idb rejects `done` on abort; that rejection is the point, not an error.
                    transaction.done.catch(() => undefined);
                    transaction.abort();
                },
            });
        } catch (error) {
            if (missing) return [];
            throw error;
        }
        try {
            if (!db.objectStoreNames.contains("transcriptSections")) return [];
            const rows = await db.getAll("transcriptSections");
            return rows.filter((r) => r.bytesUploaded < r.audioData.size);
        } finally {
            db.close();
        }
    }

    // initialize the database connection
    async init(): Promise<void> {
        // If initialization is already in progress or completed, return the existing promise
        if (AudioDb.initPromise) {
            return AudioDb.initPromise;
        }

        // Create a new initialization promise
        AudioDb.initPromise = (async () => {
            try {
                if (this.db) return; // avoid re-initialization

                this.db = await openDB<HappyDB>(this.dbName, INDEX_DB_VERSION, {
                    // on upgrade, delete old store and create new one
                    upgrade(db, oldVersion) {
                        if (oldVersion < INDEX_DB_VERSION) {
                            if (db.objectStoreNames.contains("transcriptSections")) {
                                db.deleteObjectStore("transcriptSections");
                            }
                        }

                        if (!db.objectStoreNames.contains("transcriptSections")) {
                            const store = db.createObjectStore("transcriptSections", { keyPath: "key" });
                            store.createIndex("createdAtIndex", "createdAt", { unique: false });
                        }
                    },
                    // on blocked, log an error
                    blocked(currentVersion, blockedVersion, event) {
                        Sentry.captureException(new Error(`IDB blocked: ${currentVersion} -> ${blockedVersion}`), {
                            extra: { context: "init", event },
                        });
                    },
                    blocking(currentVersion, blockingVersion, event) {
                        Sentry.captureException(new Error(`IDB blocking: ${currentVersion} -> ${blockingVersion}`), {
                            extra: { context: "init", event },
                        });
                    },
                    // on terminated, log an error and set the db to null
                    terminated: () => {
                        Sentry.captureException(new Error("IDB terminated"), { extra: { context: "init" } });

                        // not sure if this scoping will work
                        this.db = null;
                    },
                });

                // run post-initialization tasks
                await this.cleanupOldEntries();
                await this.setAllUploadsNotUploading();

                // check storage availability and try to clear some space if needed
                await this.checkStorage();
            } catch (error) {
                Sentry.captureException(error, { extra: { context: "init" } });
                // Reset initPromise on error so it can be retried
                AudioDb.initPromise = null;
                throw error;
            }
        })();

        return AudioDb.initPromise;
    }

    /**
     * Ensures the database is initialized before any operation
     * !important: await this function before any db calls
     */
    private async ensureDbInitialized(): Promise<void> {
        if (!this.db) {
            await this.init();
        }

        // throw an err if that failed :(
        if (!this.db) {
            throw new Error("Database not initialized");
        }
    }

    private async captureAudioDbException(error: any, context: string) {
        try {
            if (!this.db) {
                // The DB never opened — the diagnostics below need it, but this is precisely when a
                // persistence write is most likely failing, so surface the original error instead
                // of dropping it silently.
                Sentry.captureException(error, {
                    tags: { errorCategory: "recording", errorId: "recording.audiodb_unavailable" },
                    extra: { context, dbInitialized: false },
                });
                return;
            }

            const store = this.db!.transaction("transcriptSections").store;
            const allRecords = await store.getAll();

            const incompleteUploads = allRecords.filter((record) => record.bytesUploaded < record.audioData.size);
            const incompleteCount = incompleteUploads.length;
            const totalSizeMB = allRecords.reduce((sum, record) => sum + record.audioData.size, 0) / (1024 * 1024);

            const records = allRecords.map((record) => ({
                createdAt: new Date(record.createdAt).toISOString(),
                audioDataSize: (record.audioData.size / (1024 * 1024)).toFixed(2) + " MB",
                completed: record.bytesUploaded >= record.audioData.size,
                bytesUploaded: record.bytesUploaded,
                audioDataBytes: record.audioData.size,
                transcriptId: record.transcriptId,
                transcriptSectionId: record.transcriptSectionId,
            }));

            // Get storage estimate
            let storageEstimate = null;
            if ("storage" in navigator && "estimate" in navigator.storage) {
                try {
                    const estimate = await navigator.storage.estimate();
                    const estimateWithDetails = estimate as StorageEstimate & {
                        usageDetails?: {
                            indexedDB?: number;
                            caches?: number;
                        };
                    };
                    storageEstimate = {
                        quota: estimate.quota ? (estimate.quota / (1024 * 1024)).toFixed(2) + " MB" : null,
                        usage: estimate.usage ? (estimate.usage / (1024 * 1024)).toFixed(2) + " MB" : null,
                        usageDetails: estimateWithDetails.usageDetails
                            ? {
                                  indexedDB: estimateWithDetails.usageDetails.indexedDB
                                      ? (estimateWithDetails.usageDetails.indexedDB / (1024 * 1024)).toFixed(2) + " MB"
                                      : null,
                                  caches: estimateWithDetails.usageDetails.caches
                                      ? (estimateWithDetails.usageDetails.caches / (1024 * 1024)).toFixed(2) + " MB"
                                      : null,
                              }
                            : null,
                    };
                } catch (storageError) {
                    storageEstimate = { error: "Failed to get storage estimate", errorMessage: String(storageError) };
                }
            }

            // Get all databases and record counts
            let allDbs = null;
            try {
                if ("databases" in indexedDB) {
                    const databases = await indexedDB.databases();
                    const dbInfo = await Promise.all(
                        databases.map(async (dbInfo) => {
                            try {
                                if (!dbInfo.name) {
                                    throw new Error("Database name is missing");
                                }

                                const dbName = dbInfo.name;
                                const dbVersion = dbInfo.version;

                                // Try to open the database to get object store info
                                const db = await new Promise<IDBDatabase>((resolve, reject) => {
                                    // If version is undefined, open without specifying version (uses current version)
                                    const request =
                                        dbVersion !== undefined
                                            ? indexedDB.open(dbName, dbVersion)
                                            : indexedDB.open(dbName);
                                    request.onsuccess = () => resolve(request.result);
                                    request.onerror = () => reject(request.error);
                                    request.onblocked = () => reject(new Error("Database blocked"));
                                });

                                // Get object store names and count records in each
                                const objectStoreCounts: Array<{ name: string; count: number }> = [];
                                for (const storeName of Array.from(db.objectStoreNames)) {
                                    try {
                                        const count = await new Promise<number>((resolve, reject) => {
                                            const tx = db.transaction(storeName, "readonly");
                                            const store = tx.objectStore(storeName);
                                            const countRequest = store.count();
                                            countRequest.onsuccess = () => resolve(countRequest.result);
                                            countRequest.onerror = () => reject(countRequest.error);
                                        });
                                        objectStoreCounts.push({ name: storeName, count });
                                    } catch {
                                        objectStoreCounts.push({ name: storeName, count: -1 }); // -1 indicates error counting
                                    }
                                }

                                db.close();

                                return {
                                    name: dbInfo.name,
                                    version: dbInfo.version,
                                    objectStoreCounts,
                                };
                            } catch (dbError) {
                                return {
                                    name: dbInfo.name,
                                    version: dbInfo.version,
                                    error: String(dbError),
                                    errorMessage: dbError instanceof Error ? dbError.message : "Unknown error",
                                };
                            }
                        }),
                    );
                    allDbs = dbInfo;
                } else {
                    allDbs = { error: "indexedDB.databases() not supported in this browser" };
                }
            } catch (dbsError) {
                allDbs = { error: "Failed to get all databases", errorMessage: String(dbsError) };
            }

            Sentry.captureException(error, {
                extra: {
                    context,
                    incompleteUploads: incompleteCount,
                    records,
                    recordsCount: allRecords.length,
                    storageEstimate,
                    totalAudioDataSize: totalSizeMB.toFixed(2) + " MB",
                    allDbs,
                },
            });
        } catch (diagnosticError) {
            // If we can't collect diagnostics, at least capture the original error
            Sentry.captureException(error, {
                extra: {
                    context,
                    diagnosticError: String(diagnosticError),
                    diagnosticErrorMessage:
                        diagnosticError instanceof Error ? diagnosticError.message : "Unknown error",
                },
            });
        }
    }

    /**
     * Deletes all entries in the transcriptSections store older than 7 days
     * (3 days on iOS due to smaller storage limits)
     */
    private async cleanupOldEntries(): Promise<void> {
        try {
            await this.ensureDbInitialized();

            const deleteTimeFrame = new Date();
            if (navigator.userAgent.match(/(iPod|iPhone|iPad)/)) {
                deleteTimeFrame.setDate(deleteTimeFrame.getDate() - 3); // iOS: 3 days
            } else {
                deleteTimeFrame.setDate(deleteTimeFrame.getDate() - 7); // everything else: 7 days
            }

            const tx = this.db!.transaction("transcriptSections", "readwrite");
            const store = tx.objectStore("transcriptSections");
            let cursor = await store.openCursor();

            while (cursor) {
                if (new Date(cursor.value.createdAt) < deleteTimeFrame) {
                    await cursor.delete();
                }

                cursor = await cursor.continue();
            }

            await tx.done;
        } catch (error) {
            await this.captureAudioDbException(error, "cleanupOldEntries");
        }
    }

    /**
     * Set all uploads to not uploading
     */
    private async setAllUploadsNotUploading(): Promise<void> {
        try {
            await this.ensureDbInitialized();

            const tx = this.db!.transaction("transcriptSections", "readwrite");
            const store = tx.objectStore("transcriptSections");
            let cursor = await store.openCursor();

            while (cursor) {
                const record = cursor.value;
                record.isUploading = false;
                await cursor.update(record);
                cursor = await cursor.continue();
            }

            await tx.done;
        } catch (error) {
            await this.captureAudioDbException(error, "setAllUploadsNotUploading");
        }
    }

    /**
     * Gets the estimated available storage quota in MB
     * @returns Available storage in MB, or null if unavailable
     */
    private async getStorageEstimate(): Promise<number | null> {
        if (!("storage" in navigator && "estimate" in navigator.storage)) {
            return null;
        }

        try {
            const estimate = await navigator.storage.estimate();
            if (estimate.quota != null && estimate.usage != null) {
                const availableSpace = estimate.quota - estimate.usage;
                const availableSpaceMB = availableSpace / (1024 * 1024);
                return availableSpaceMB;
            }
            return null;
        } catch (storageError) {
            // Log but don't fail if storage estimate is not available
            Sentry.captureException(storageError, { extra: { context: "getStorageEstimate - storage estimate" } });
            return null;
        }
    }

    /**
     * Check if the browser supports saving audio files by saving a test audio blob
     * Also checks storage quota and cleans up pendo databases if needed
     * Shows appropriate toast messages for errors
     */
    async checkStorage(): Promise<void> {
        try {
            await this.ensureDbInitialized();

            const testKey = "audioBlobTest/HappyDocTest";
            const audioBlob = new Blob(["test"], { type: "audio/wav" });
            const tx = this.db!.transaction("transcriptSections", "readwrite");
            const store = tx.objectStore("transcriptSections");

            await store.put({ key: testKey, audioData: audioBlob } as any);
            await store.delete(testKey);

            await tx.done;

            // Try to clear up storage space if needed, showing warnings if space is still low
            const availableSpaceMB = await this.ensureAvailableStorage();
            if (availableSpaceMB !== null && availableSpaceMB < LOW_DISK_SPACE_THRESHOLD) {
                // If less than 150MB available, show warning
                toast.warning(
                    "LOW DISK SPACE WARNING! Please free up space or recordings may be lost if you go offline.",
                    { autoClose: false },
                );
                this.captureAudioDbException(
                    new Error("low disk space warning"),
                    "checkStorage - low disk space warning",
                );
            }

            // Success - no action needed
            return;
        } catch (error) {
            // Check if it's a QuotaExceededError
            if (error instanceof DOMException && error.name === "QuotaExceededError") {
                // Try to clear up storage space
                Sentry.addBreadcrumb({
                    category: "checkStorage",
                    message: "Handling QuotaExceededError - trying to clear up storage space",
                    level: "info",
                });
                const availableSpaceMB = await this.ensureAvailableStorage();

                if (availableSpaceMB !== null && availableSpaceMB < LOW_DISK_SPACE_THRESHOLD) {
                    if (availableSpaceMB < 10) {
                        toast.error(
                            "NO DISK SPACE AVAILABLE!\n Please free up space or recordings may be lost if you go offline.",
                            { autoClose: false },
                        );
                        await this.captureAudioDbException(
                            error,
                            "checkStorage - QuotaExceededError - no disk space after cleanup",
                        );
                    } else {
                        toast.warning(
                            "LOW DISK SPACE WARNING! Please free up space or recordings may be lost if you go offline.",
                            { autoClose: false },
                        );
                        await this.captureAudioDbException(
                            error,
                            "checkStorage - QuotaExceededError - low disk space after cleanup",
                        );
                    }
                }
                return;
            }

            // Other errors - no support
            toast.error(
                "Uh oh! This browser may not support saving audio files and may degrade performance. Please ensure you are not in Private Browsing or Incognito mode. See Help Center or reach out to support for more help.",
                { autoClose: false },
            );
            await this.captureAudioDbException(error, "checkStorage - not supported");
        }
    }

    /**
     * Cleans up space by deleting pendo databases and old transcript sections
     * Attempts to ensure at least LOW_DISK_SPACE_THRESHOLD of available storage by deleting
     * oldest entries first. Never deletes the newest entry
     * @returns the available space in MB
     */
    private async ensureAvailableStorage({
        spaceNeededMB = LOW_DISK_SPACE_THRESHOLD,
        canDeleteIncompleteUploads = false,
        canDeleteLatestEntry = false,
    } = {}): Promise<number | null> {
        let availableSpaceMB = await this.getStorageEstimate();
        if (availableSpaceMB !== null && availableSpaceMB >= spaceNeededMB) {
            // Already have enough space, no cleanup needed
            return availableSpaceMB;
        }
        Sentry.addBreadcrumb({
            category: "ensureAvailableStorage",
            message: "Free space below threshold. Starting cleanup.",
            level: "info",
            data: {
                availableSpaceMB: availableSpaceMB ? availableSpaceMB.toFixed(2) + " MB" : "null",
                spaceNeededMB: spaceNeededMB.toFixed(2) + " MB",
                canDeleteLatestEntry: canDeleteLatestEntry,
            },
        });

        // First, delete pendo databases
        await this.deletePendoDatabases();

        // Check storage again after deleting pendo databases
        availableSpaceMB = await this.getStorageEstimate();
        if (availableSpaceMB !== null && availableSpaceMB >= spaceNeededMB) {
            // Enough space after deleting pendo databases
            Sentry.addBreadcrumb({
                category: "ensureAvailableStorage",
                message: "Enough space after deleting pendo databases",
                level: "info",
                data: {
                    availableSpaceMB: availableSpaceMB ? availableSpaceMB.toFixed(2) + " MB" : "null",
                    spaceNeededMB: spaceNeededMB.toFixed(2) + " MB",
                    canDeleteLatestEntry: canDeleteLatestEntry,
                },
            });
            return availableSpaceMB;
        }

        // Need to delete transcript sections
        try {
            await this.ensureDbInitialized();

            const tx = this.db!.transaction("transcriptSections", "readwrite");
            const store = tx.objectStore("transcriptSections");
            const index = store.index("createdAtIndex");

            // Get all entries sorted by creation date (oldest first)
            const allEntries: Array<{ key: string; value: TranscriptSectionIndexDB }> = [];
            let cursor = await index.openCursor();

            let totalAudioDataSizeMB = 0;
            while (cursor) {
                totalAudioDataSizeMB += cursor.value.audioData.size / (1024 * 1024);
                allEntries.push({
                    key: cursor.primaryKey as string,
                    value: cursor.value,
                });
                cursor = await cursor.continue();
            }

            // If we have 0 or 1 entries, nothing to delete (never delete the newest)
            if (allEntries.length <= (canDeleteLatestEntry ? 0 : 1)) {
                await tx.done;
                Sentry.addBreadcrumb({
                    category: "ensureAvailableStorage",
                    message: "Not enough space. Cannot delete any transcript sections",
                    level: "info",
                    data: {
                        availableSpaceMB: availableSpaceMB ? availableSpaceMB.toFixed(2) + " MB" : "null",
                        totalAudioDataSizeBeforeCleanupMB: totalAudioDataSizeMB.toFixed(2) + " MB",
                        transcriptSectionsCount: allEntries.length,
                        spaceNeededMB: spaceNeededMB.toFixed(2) + " MB",
                        canDeleteLatestEntry: canDeleteLatestEntry,
                    },
                });
                return availableSpaceMB;
            }

            // Sort by completed status (completed first) and then createdAt (oldest first)
            allEntries.sort((a, b) => {
                const aCompleted = a.value.bytesUploaded >= a.value.audioData.size;
                const bCompleted = b.value.bytesUploaded >= b.value.audioData.size;

                if (aCompleted !== bCompleted) {
                    return aCompleted ? -1 : 1;
                }

                const dateA = new Date(a.value.createdAt).getTime();
                const dateB = new Date(b.value.createdAt).getTime();
                return dateA - dateB;
            });

            const deletableEntries = allEntries
                // Do not delete incomplete uploads if canDeleteIncompleteUploads is false
                .filter(
                    (entry) => canDeleteIncompleteUploads || entry.value.bytesUploaded >= entry.value.audioData.size,
                )
                // Do not delete the newest entry (last in sorted array) if canDeleteLatestEntry is true
                .slice(0, canDeleteLatestEntry ? undefined : -1);

            // Delete oldest entries (completed first) until we have at least spaceNeededMB available
            let deletedCount = 0;
            availableSpaceMB = availableSpaceMB || 0;
            for (const entry of deletableEntries) {
                // Use a separate transaction for each delete to avoid transaction auto-commit
                const tx2 = this.db!.transaction("transcriptSections", "readwrite");
                const store2 = tx2.objectStore("transcriptSections");
                // Delete this entry
                await store2.delete(entry.key);
                await tx2.done;
                deletedCount++;

                // Check current available space
                // Use audioData size because navigator.storage.estimate() does not update immediately
                availableSpaceMB += entry.value.audioData.size / (1024 * 1024);
                if (availableSpaceMB >= spaceNeededMB) {
                    // We've reached the target, stop deleting
                    break;
                }
            }

            Sentry.addBreadcrumb({
                category: "ensureAvailableStorage",
                message: "Available space after deleting transcript sections",
                level: "info",
                data: {
                    availableSpaceMB: availableSpaceMB.toFixed(2) + " MB",
                    totalAudioDataSizeBeforeCleanupMB: totalAudioDataSizeMB.toFixed(2) + " MB",
                    transcriptSectionsCount: allEntries.length,
                    deletedCount,
                    spaceNeededMB: spaceNeededMB.toFixed(2) + " MB",
                    canDeleteLatestEntry: canDeleteLatestEntry,
                },
            });
            return availableSpaceMB;
        } catch (error) {
            await this.captureAudioDbException(error, "ensureAvailableStorage");
            return null;
        }
    }

    /**
     * Deletes all IndexedDB databases whose names start with "pendo"
     */
    private async deletePendoDatabases(): Promise<void> {
        try {
            if ("databases" in indexedDB) {
                const databases = await indexedDB.databases();
                const pendoDatabases = databases.filter((db) => db.name && db.name.startsWith("pendo"));

                // Delete each pendo database
                for (const dbInfo of pendoDatabases) {
                    if (dbInfo.name) {
                        try {
                            await new Promise<void>((resolve, reject) => {
                                const deleteRequest = indexedDB.deleteDatabase(dbInfo.name!);
                                deleteRequest.onsuccess = () => resolve();
                                deleteRequest.onerror = () => reject(deleteRequest.error);
                                deleteRequest.onblocked = () => {
                                    // If blocked, wait a bit and try to resolve anyway
                                    setTimeout(() => resolve(), 100);
                                };
                            });
                        } catch (deleteError) {
                            // Log but don't fail on individual database deletion errors
                            Sentry.captureException(deleteError, {
                                extra: {
                                    context: "deletePendoDatabases",
                                    databaseName: dbInfo.name,
                                },
                            });
                        }
                    }
                }
            }
        } catch (error) {
            // Log but don't fail if we can't enumerate databases
            Sentry.captureException(error, { extra: { context: "deletePendoDatabases" } });
        }
    }

    /**
     * Adds a new audio blob to the database with the given details
     * @param transcriptId
     * @param transcriptSectionId
     * @param blob
     * @param audioName
     * @param bytesUploaded
     * @param patientRecordId
     * @param patientName
     * @param doctorId
     * @param isUploading
     * @param targetMonitoringDocumentId
     * @returns true if the audio was added successfully
     */
    async addTranscriptSectionAudio(
        transcriptId: string,
        transcriptSectionId: string,
        blob: Blob,
        audioName: string,
        bytesUploaded: number,
        patientRecordId: string,
        patientName: string = "",
        doctorId: string = "",
        isUploading: boolean = false,
        targetMonitoringDocumentId: string | null = null,
        accountId: string | null = null,
        // EN-1609: who owns this row for recovery. Defaults to this tab, now. The override is the hand-off
        // path for a recording owned by no live tab: a foreign nonce and a backdated snapshotAt make the
        // row immediately eligible for recovery. `manualRetryOnly` keeps a row that can't upload (no
        // transcript id) out of automatic recovery: it starts at RETRY_LIMIT, so it is only listed.
        ownership?: { ownerTabNonce: string; snapshotAt: Date; manualRetryOnly?: boolean },
    ): Promise<boolean> {
        try {
            await this.ensureDbInitialized();

            // If there is not enough space for the audio, try to free up space
            const availableSpaceMB = await this.getStorageEstimate();
            const audioSizeMB = blob.size / (1024 * 1024);
            if ((availableSpaceMB || 0) - audioSizeMB < 1) {
                Sentry.addBreadcrumb({
                    category: "addTranscriptSectionAudio",
                    message: "Not enough space for audio. Trying to free up space.",
                    level: "info",
                    data: {
                        availableSpaceMB: availableSpaceMB ? availableSpaceMB.toFixed(2) + " MB" : "null",
                        audioSizeMB: audioSizeMB.toFixed(2) + " MB",
                    },
                });
                await this.ensureAvailableStorage({
                    spaceNeededMB: audioSizeMB,
                    canDeleteIncompleteUploads: true,
                    canDeleteLatestEntry: true,
                });
            }

            const key = `${transcriptId}/${transcriptSectionId}`;
            const data = {
                key,
                audioData: blob,
                fileName: audioName,
                createdAt: new Date(),
                transcriptId,
                transcriptSectionId,
                bytesUploaded,
                patientRecordId,
                uploadUrl: null,
                patientName,
                doctorId,
                // EN-836/EN-652: the stop path persists the final file with isUploading=true so a
                // concurrent recovery pass (retryIncompleteUploads skips isUploading rows) can't
                // upload the same section in parallel during the stop→upload handoff. Mid-recording
                // snapshots pass false — they must stay recoverable while recording continues.
                isUploading,
                retryCount: ownership?.manualRetryOnly ? RETRY_LIMIT : 0,
                targetMonitoringDocumentId,
                accountId,
                ownerTabNonce: ownership?.ownerTabNonce ?? getTabNonce(),
                snapshotAt: ownership?.snapshotAt ?? new Date(),
            };

            await this.db!.put("transcriptSections", data);
            return true;
        } catch (error) {
            await this.captureAudioDbException(error, "addTranscriptSectionAudio");
            return false;
        }
    }

    /**
     * EN-1609: re-stamps a row's snapshotAt to now, touching nothing else, so a paused session (which
     * writes no snapshots) keeps its row from looking abandoned to other tabs' recovery. No-op if the
     * row doesn't exist. Never throws.
     */
    async touchSnapshot(transcriptId: string, transcriptSectionId: string): Promise<void> {
        try {
            await this.ensureDbInitialized();
            const tx = this.db!.transaction("transcriptSections", "readwrite");
            const row = await tx.store.get(`${transcriptId}/${transcriptSectionId}`);
            if (row) await tx.store.put({ ...row, snapshotAt: new Date() });
            await tx.done;
        } catch (error) {
            await this.captureAudioDbException(error, "touchSnapshot");
        }
    }

    /**
     * Updates the upload state of a transcript section
     * @param transcriptId
     * @param transcriptSectionId
     * @param uploadUrl
     * @param bytesUploaded
     * @param isUploading
     * @param retryCount
     * @returns true if the transcript section was updated successfully
     */
    async updateUploadState(
        transcriptId: string,
        transcriptSectionId: string,
        uploadUrl: string,
        bytesUploaded?: number,
        isUploading?: boolean,
        retryCount?: number,
    ): Promise<{ error: null; success: true } | { error: Error; success: false }> {
        try {
            await this.ensureDbInitialized();

            // get existing
            const key = `${transcriptId}/${transcriptSectionId}`;
            const data = await this.db!.get("transcriptSections", key);

            // record not found, return false
            if (!data) {
                Sentry.addBreadcrumb({
                    category: "uploadAudio",
                    message: "Transcript section not found in updateUploadState",
                    level: "warning",
                    data: {
                        transcriptId,
                        transcriptSectionId,
                    },
                });
                return { success: false, error: new Error("Transcript section not found in updateUploadState") };
            }

            // apply updates
            if (bytesUploaded !== undefined) data.bytesUploaded = bytesUploaded;
            if (isUploading !== undefined) data.isUploading = isUploading;
            if (retryCount !== undefined) data.retryCount = retryCount;
            data.uploadUrl = uploadUrl;

            // save
            await this.db!.put("transcriptSections", data);

            return { success: true, error: null };
        } catch (error) {
            if (error instanceof Error) {
                await this.captureAudioDbException(error, "updateUploadState");
                return { success: false, error: error };
            } else {
                return { success: false, error: new Error("Unknown error") };
            }
        }
    }

    /**
     * EN-1609: claims a row for a recovery upload. Re-reads it and sets isUploading in one readwrite
     * transaction, which IndexedDB never runs alongside another tab's on the same store, so two tabs'
     * racing passes can't both claim it. False when the row is gone, already claimed, or the claim
     * failed: the caller skips it, and must not release a claim it doesn't hold.
     */
    private async claimForRecovery(transcriptId: string, transcriptSectionId: string): Promise<boolean> {
        try {
            await this.ensureDbInitialized();
            const tx = this.db!.transaction("transcriptSections", "readwrite");
            const row = await tx.store.get(`${transcriptId}/${transcriptSectionId}`);
            if (!row || row.isUploading) {
                await tx.done;
                return false;
            }
            row.isUploading = true;
            row.uploadUrl = "";
            await tx.store.put(row);
            await tx.done;
            return true;
        } catch (error) {
            await this.captureAudioDbException(error, "claimForRecovery");
            return false;
        }
    }

    /**
     * Get all recordings from the db sorted by creation date (newest first)
     * @param options - Optional filter options for upload completion status
     * @returns a list of recording records sorted by creation date (newest first)
     */
    async getRecordings(options: { uploadComplete?: boolean } = {}): Promise<TranscriptSectionIndexDB[]> {
        try {
            await this.ensureDbInitialized();

            const store = this.db!.transaction("transcriptSections").store;
            const index = store.index("createdAtIndex");
            // Get all entries sorted by creation date (newest first)
            let cursor = await index.openCursor(null, "prev");

            const allEntries: TranscriptSectionIndexDB[] = [];
            while (cursor) {
                // Filter by upload completion status if options are provided
                if (
                    options.uploadComplete === undefined ||
                    (options.uploadComplete === true && cursor.value.bytesUploaded >= cursor.value.audioData.size) ||
                    (options.uploadComplete === false && cursor.value.bytesUploaded < cursor.value.audioData.size)
                ) {
                    allEntries.push(cursor.value);
                }
                cursor = await cursor.continue();
            }

            return allEntries;
        } catch (error) {
            Sentry.captureException(error, { extra: { context: "getRecordings" } });
            return [];
        }
    }

    /**
     * Get a given transcript section record from the db
     * @param transcriptId
     * @param transcriptSectionId
     * @returns a specific audio blob from the db or undefined if not found
     */
    async getTranscriptSectionAudio(
        transcriptId: string,
        transcriptSectionId: string,
    ): Promise<TranscriptSectionIndexDB | undefined> {
        try {
            await this.ensureDbInitialized();

            const key = `${transcriptId}/${transcriptSectionId}`;
            return await this.db!.get("transcriptSections", key);
        } catch (error) {
            Sentry.captureException(error, { extra: { context: "getTranscriptSectionAudio" } });
            return undefined;
        }
    }

    /**
     * Deletes a specific audio blob from the db
     * @param transcriptId
     * @param transcriptSectionId
     * @returns true if the audio was deleted successfully
     */
    async deleteTranscriptSectionAudio(transcriptId: string, transcriptSectionId: string): Promise<boolean> {
        try {
            await this.ensureDbInitialized();

            const key = `${transcriptId}/${transcriptSectionId}`;
            await this.db!.delete("transcriptSections", key);
            return true;
        } catch (error) {
            Sentry.captureException(error, { extra: { context: "deleteTranscriptSectionAudio" } });
            return false;
        }
    }

    /**
     * Handles uploaded transcript section audio by checking available storage space.
     * If available space is below LOW_DISK_SPACE_THRESHOLD, deletes the transcript section.
     * @param transcriptId
     * @param transcriptSectionId
     * @returns true if the audio was handled successfully (deleted if low space, or not deleted if enough space)
     */
    async handleUploadedTranscriptSectionAudio(transcriptId: string, transcriptSectionId: string): Promise<boolean> {
        try {
            const availableSpaceMB = await this.ensureAvailableStorage();
            // Pad the low space threshold to avoid spamming the user with warnings
            const paddedLowDiskSpaceThresholdMB = LOW_DISK_SPACE_THRESHOLD + 50;

            if (availableSpaceMB === null || availableSpaceMB < paddedLowDiskSpaceThresholdMB) {
                return await this.deleteTranscriptSectionAudio(transcriptId, transcriptSectionId);
            }

            return true;
        } catch (error) {
            Sentry.captureException(error, { extra: { context: "handleUploadedTranscriptSectionAudio" } });
            return false;
        }
    }

    /**
     * Deletes all audio blobs associated with a patient record
     * @param patientRecordId
     * @returns true if the audio was deleted successfully
     */
    async deleteAudioUsingPatientRecord(patientRecordId: string): Promise<boolean> {
        try {
            await this.ensureDbInitialized();

            const store = this.db!.transaction("transcriptSections", "readwrite").store;
            let cursor = await store.openCursor();

            while (cursor) {
                if (cursor.value.patientRecordId === patientRecordId) {
                    await cursor.delete();
                }

                cursor = await cursor.continue();
            }

            return true;
        } catch (error) {
            Sentry.captureException(error, { extra: { context: "deleteAudioUsingPatientRecord" } });
            return false;
        }
    }

    /**
     * Retries incomplete uploads
     * @param transcriptId - optional transcript id to filter by
     * @param transcriptSectionId - optional transcript section id to filter by
     * @param isStartup - optional boolean to indicate if this is a startup retry
     * @returns new state of incomplete uploads, successful uploads are removed from the list
     */
    async retryIncompleteUploads(
        transcriptId?: string,
        transcriptSectionId?: string,
        isStartup?: boolean,
        // PR review finding: incomplete.accountId is only populated for recordings persisted after
        // EN-1216 added the field -- older rows (queued before that deploy) read back as undefined
        // and, unguarded, would hard-fail this retry forever (the refuse-without-accountId guards
        // that PR added have no fallback of their own, and this is a plain class method with no
        // React context access to derive one). The caller (DataProvider, which does have account
        // context) passes the live account id here; only used when the recording itself never
        // captured one, so a real captured incomplete.accountId always wins below.
        fallbackAccountId?: string | null,
        // EN-1609: the other tabs currently recording (recorderPersistence findLiveRecordingTabs). When
        // given, each row is recovered only if its owner isn't live and its snapshot is stale, so a
        // closed tab's recording uploads even while other tabs record. Omitted: every row is eligible
        // (pre-EN-1609 behavior, for callers that already know nothing is recording).
        liveOwners?: { nonces: Set<string>; legacy: boolean },
    ) {
        // A pre-EN-1609 tab is recording: its PONG has no nonce, so we can't tell which rows it owns.
        if (liveOwners?.legacy) {
            return;
        }
        // Serialize recovery passes. The startup and network-restore callers (EN-836) can overlap
        // (e.g. the app boots offline, startup recovery begins, then `online` fires mid-pass);
        // without this guard both read the same not-yet-deleted rows and upload each one twice —
        // duplicate transcript_section + duplicate /generate/push (the EN-652 class).
        if (this.retryInFlight) {
            return;
        }
        this.retryInFlight = true;
        try {
            let incompleteUploads = await this.getRecordings({ uploadComplete: false });
            if (transcriptId) {
                incompleteUploads = incompleteUploads.filter(
                    (incomplete) =>
                        incomplete.transcriptId === transcriptId &&
                        (incomplete.transcriptSectionId === transcriptSectionId || !transcriptSectionId),
                );
            }

            // EN-1609: whether the page this tab showed before the current load is gone. A reload or crash
            // leaves it gone; a duplicated tab inherits the still-open original's nonce as its "previous"
            // one, and that original may be uploading these rows itself (this tab's init already cleared
            // their isUploading flags). Asked only when such a row is waiting.
            const previousNonce = getPreviousTabNonce();
            const previousPageGone =
                !!previousNonce &&
                incompleteUploads.some((row) => row.ownerTabNonce === previousNonce) &&
                !(await isTabOpen(previousNonce));

            // EN-1609: whether no live tab may still be writing this row. This tab's own rows need no
            // special case: a tab never answers its own ping, and the caller's
            // shouldRecoverIncompleteUploads gate already stops recovery while this tab records, so
            // this tab's leftovers from an earlier session recover under the same staleness rule.
            const isOwnerGone = (incomplete: TranscriptSectionIndexDB): boolean => {
                if (!liveOwners) return true;
                // Pre-EN-1609 row: owner unknown, so keep the old rule (blocked while any tab records).
                if (!incomplete.ownerTabNonce || !incomplete.snapshotAt) return liveOwners.nonces.size === 0;
                if (liveOwners.nonces.has(incomplete.ownerTabNonce)) return false;
                // The page this tab showed before a reload/crash is gone, so no need to wait out the
                // staleness window. Only once isTabOpen found it gone (see previousPageGone above).
                if (previousPageGone && incomplete.ownerTabNonce === previousNonce) return true;
                return Date.now() - incomplete.snapshotAt.getTime() >= STALE_OWNER_MS;
            };

            const deadRows: string[] = []; // fileName of each row found dead this pass, for one summary toast

            // PR review finding: this predicate must exactly match the loop's own two
            // early-continue conditions below (it's what "eligible for the pre-pass integrity
            // check" means) -- shared here, not duplicated by hand, specifically so the two can't
            // silently drift apart. If they did, a row could reach `integrityByRow.get(incomplete)!`
            // without ever having had its integrity precomputed, throwing on the `!` assertion.
            const isEligibleForRetry = (incomplete: TranscriptSectionIndexDB): boolean =>
                !(isStartup && incomplete.retryCount >= RETRY_LIMIT) &&
                !incomplete.isUploading &&
                isOwnerGone(incomplete);

            // PR review finding: awaiting verifyAudioBlobIntegrity (a blob.slice().arrayBuffer()
            // read, up to 64KB) serially inside the loop below extends how long every row's
            // isUploading claim is held and delays the whole pass, worst on the exact "many stuck
            // rows" scenario this feature targets. The reads themselves are pure (no IndexedDB
            // writes, can't race with anything) and verifyAudioBlobIntegrity never rejects (it
            // catches its own read failures and resolves `dead`/read_failed instead), so it's safe
            // to run them all concurrently up front -- only for rows that will actually reach the
            // check below, so this doesn't do wasted reads for rows the loop would skip anyway.
            // The loop's own per-row work (claiming, uploading, deleting) stays fully sequential;
            // only this read is batched.
            const eligibleForIntegrityCheck = incompleteUploads.filter(isEligibleForRetry);
            const integrityByRow = new Map<TranscriptSectionIndexDB, AudioIntegrityResult>(
                await Promise.all(
                    eligibleForIntegrityCheck.map(
                        async (incomplete): Promise<[TranscriptSectionIndexDB, AudioIntegrityResult]> => [
                            incomplete,
                            await verifyAudioBlobIntegrity(incomplete.audioData),
                        ],
                    ),
                ),
            );

            for (const incomplete of incompleteUploads) {
                try {
                    if (!isEligibleForRetry(incomplete)) {
                        continue;
                    }

                    // Mark in-progress *before* the upload so any later pass (or a resumed session)
                    // skips this row instead of re-uploading it. init() calls
                    // setAllUploadsNotUploading(), so a crash mid-upload can't strand the row
                    // permanently — the flag is cleared on the next startup. EN-1609: claimed
                    // atomically, since this row was read at pass start and another tab's pass may
                    // have claimed (or uploaded and deleted) it since.
                    if (!(await this.claimForRecovery(incomplete.transcriptId, incomplete.transcriptSectionId))) {
                        continue;
                    }

                    // EN-1217: a row can pass persist-time verification and still die later — WebKit's
                    // backing-file reclaim is a storage-pressure eviction, not tied to any single call site.
                    // There is no recovery action for a dead blob, so delete the row instead of retrying
                    // forever. Precomputed above (in parallel across all eligible rows), not awaited here.
                    const integrity = integrityByRow.get(incomplete)!;
                    if (integrity.kind === "dead") {
                        Sentry.captureException(new Error("recording.dead_blob_detected_on_retry"), {
                            tags: { errorCategory: "recording", errorId: "recording.dead_blob_detected_on_retry" },
                            extra: buildDeadBlobSentryExtra(integrity, {
                                transcriptId: incomplete.transcriptId,
                                transcriptSectionId: incomplete.transcriptSectionId,
                                claimedSize: incomplete.audioData.size,
                            }),
                        });
                        deadRows.push(incomplete.fileName);
                        try {
                            await this.deleteTranscriptSectionAudio(
                                incomplete.transcriptId,
                                incomplete.transcriptSectionId,
                            );
                        } catch (deleteError) {
                            // Isolated: a delete failure here is a distinct, separately-diagnosable problem from
                            // the integrity failure already captured above. Falling through to the outer catch
                            // would bump retryCount and re-attempt UPLOADING a blob we already know is dead --
                            // wrong; we just want another chance to clean up the row, not to retry uploading it.
                            Sentry.captureException(deleteError, {
                                tags: { errorCategory: "recording", errorId: "recording.dead_blob_delete_failed" },
                                extra: {
                                    transcriptId: incomplete.transcriptId,
                                    transcriptSectionId: incomplete.transcriptSectionId,
                                },
                            });
                            // Release the isUploading claim set above -- without this the row is stuck
                            // (the top of this loop skips isUploading rows) until the next app startup's
                            // setAllUploadsNotUploading() reset, not "another chance to clean up the row"
                            // on the very next pass as intended.
                            //
                            // Also bump retryCount, reusing this method's existing RETRY_LIMIT gate
                            // (top of this loop): a persistently-failing delete (quota pressure, storage
                            // corruption, cross-tab contention) would otherwise re-run this whole branch
                            // -- integrity check, Sentry capture, summary toast -- on every future pass
                            // forever. retryIncompleteUploads' only production caller (DataProvider.tsx)
                            // always passes isStartup=true for both the startup AND network-restore
                            // triggers, so that gate already gives up re-alerting after RETRY_LIMIT
                            // attempts; it just needed this path to increment the counter too.
                            incomplete.retryCount = incomplete.retryCount + 1;
                            await this.updateUploadState(
                                incomplete.transcriptId,
                                incomplete.transcriptSectionId,
                                "",
                                incomplete.bytesUploaded,
                                false,
                                incomplete.retryCount,
                            );
                        }
                        continue;
                    }

                    // try traditional upload. Pass the target Monitoring document persisted alongside
                    // this recording (see TranscriptSectionIndexDB) — not some live ref, since this
                    // automatic recovery pass runs independently of whatever recording/tab is active.
                    const { error } = await uploadAndTranscribeBulkAudio(
                        incomplete.transcriptId,
                        incomplete.transcriptSectionId,
                        incomplete.audioData,
                        incomplete.fileName,
                        incomplete.patientRecordId,
                        incomplete?.patientName || "",
                        "",
                        [],
                        incomplete.targetMonitoringDocumentId ?? null,
                        incomplete.accountId ?? fallbackAccountId ?? null,
                    );
                    if (error) {
                        throw error;
                    }

                    // successful, get rid of the incomplete record
                    await this.deleteTranscriptSectionAudio(incomplete.transcriptId, incomplete.transcriptSectionId);
                } catch (error: any) {
                    // failed, clear the in-progress flag and bump the retry count
                    incomplete.retryCount = incomplete.retryCount + 1;
                    await this.updateUploadState(
                        incomplete.transcriptId,
                        incomplete.transcriptSectionId,
                        "",
                        incomplete.bytesUploaded,
                        false,
                        incomplete.retryCount,
                    );

                    Sentry.captureException(error, {
                        tags: {
                            errorCategory: "recording",
                            errorId: "recording.retry_incomplete_upload_failed",
                        },
                    });
                }
            }

            if (deadRows.length > 0) {
                const summary =
                    deadRows.length === 1
                        ? `A saved recording (${deadRows[0]}) was lost by the browser and can't be recovered.`
                        : `${deadRows.length} saved recordings were lost by the browser and can't be recovered.`;
                toast.error(summary, { autoClose: false });
            }
        } finally {
            this.retryInFlight = false;
        }
    }
}

export default AudioDb;
